'use client';

import type { Data } from '@repo/strapi-types';
import { ArrowLeft } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { Container } from '@/components/elementary/Container';
import LocaleSwitcher from '@/components/elementary/LocaleSwitcher/LocaleSwitcher';
import { DesktopNavigation } from '@/components/elementary/navbar/desktop/DesktopNavigation';
import { MobileMenu } from '@/components/elementary/navbar/mobile/MobileMenu';
import { MobileMenuToggle } from '@/components/elementary/navbar/mobile/MobileMenuToggle';
import { Button } from '@/components/ui/button';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { useIsScrolled } from '@/hooks/useIsScrolled';
import { useIsMobile } from '@/hooks/useMobile';
import { MOBILE_MENU_ANIMATON_DURATION } from '@/lib/constants';
import { cn } from '@/lib/styles';
import { NAVBAR_TEST_IDS } from '@/lib/test-ids';
import type { AppLocale } from '@/types/general';
import type { LocaleLink } from '@/types/i18n.types';
import { Logo } from './Logo';
interface NavbarProps {
  navbarData: Data.ContentType<'api::navbar.navbar'>;
  categories: Data.ContentType<'api::blog-category.blog-category'>[];
  locale: AppLocale;
  minimal?: boolean;
  localeLinks?: LocaleLink[];
  transparent?: boolean;
}
export function Navbar({
  navbarData,
  categories,
  locale,
  minimal,
  localeLinks,
  transparent
}: NavbarProps) {
  const t = useTranslations('Navbar');
  const mobileMenuRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeNavMenuContent, setActiveNavMenuContent] = useState<Data.Component<'layout-utilities.navbar-item'> | null>(null);
  const [isOpenDropDown, setIsOpenDropDown] = useState(false);
  const {
    items,
    login,
    buttons,
    dropdown,
    logo
  } = navbarData || {};
  const isScrolled = useIsScrolled();

  // Show border when scrolled OR when the mobile menu is open
  const shouldShowBorder = isScrolled || isMobileMenuOpen;

  // Show transparent bg when `transparent` is set and the border isn't showing
  const isTransparent = transparent && !shouldShowBorder;

  // Lock body scroll when mobile menu is open
  useBodyScrollLock(isMobileMenuOpen && isMobile, mobileMenuRef.current ?? undefined);
  const scrollToTop = () => {
    mobileMenuRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const showBackButton = isMobileMenuOpen && (activeNavMenuContent || isOpenDropDown) && isMobile;
  const handleBackClick = () => {
    // If in a subcategory or dropdown, go back to main navigation
    if (activeNavMenuContent || isOpenDropDown) {
      setActiveNavMenuContent(null);
      setIsOpenDropDown(false);
      // Scroll to top after state change
      setTimeout(() => scrollToTop(), 0);
    } else {
      // If in main navigation, close the mobile menu
      setIsMobileMenuOpen(false);
    }
  };
  const handleMenuToggle = () => {
    // If mobile menu is closed, show default navbar with logo
    if (isMobileMenuOpen) {
      setActiveNavMenuContent(null);
      setIsOpenDropDown(false);
      // Scroll to top after state change, wait for menu close animation to finish
      setTimeout(() => scrollToTop(), MOBILE_MENU_ANIMATON_DURATION);
    }
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  return <>
			{/* Fixed border that spans full width */}
			<div className={cn('fixed z-51 lg:z-9 w-full top-(--site-header-offset-mobile) lg:top-(--site-header-offset-desktop) transition-colors h-px', shouldShowBorder ? 'bg-neutral-300' : 'bg-transparent', isMobileMenuOpen ? 'duration-500' : 'duration-300')} />

			<div className="fixed top-(--announcement-banner-height) w-full z-50" data-test-id={NAVBAR_TEST_IDS.COMPONENT}>
				<header className={cn('transition-colors duration-200', isTransparent ? 'bg-transparent' : 'bg-neutral-light-100')}>
					<Container className="py-5 lg:py-7.25 flex justify-between items-center" paddingX>
						<div className="flex items-center gap-8 w-full lg:py-0.75">
							{/* Logo & Mobile menu back button */}
							{showBackButton ? <Button className="flex gap-6" size="none" variant="primaryLink" onClick={handleBackClick}>
									<ArrowLeft className="size-6" />
									<span className="text-inherit text-4xl/6">{t('back')}</span>
								</Button> : <Logo logo={logo} />}

							{/* Desktop Navigation */}
							<DesktopNavigation items={items} buttons={buttons} dropdown={dropdown} login={login} minimal={minimal} />
						</div>

						{/* Locale Switcher */}
						{localeLinks && localeLinks.length > 1 && <div className="hidden lg:block ml-4">
								<LocaleSwitcher currentLocale={locale} localeLinks={localeLinks} />
							</div>}

						{/* Mobile menu toggle */}
						<MobileMenuToggle onClick={handleMenuToggle} isMobileMenuOpen={isMobileMenuOpen} />
					</Container>
				</header>

				{/* Mobile Navigation */}
				{navbarData || categories ? <MobileMenu ref={mobileMenuRef} isMobileMenuOpen={isMobileMenuOpen} resetMobileMenu={handleMenuToggle} navbarData={navbarData} categories={categories} activeNavMenuContent={activeNavMenuContent} setActiveNavMenuContent={setActiveNavMenuContent} isOpenDropDown={isOpenDropDown} setIsOpenDropDown={setIsOpenDropDown} locale={locale} localeLinks={localeLinks} /> : null}
			</div>
		</>;
}